import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Output,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { take } from 'rxjs/operators';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

import type { AnalyticsSettingsLegacy } from '@sites/entity-core';

import { EPixelTypes } from '@common/types/analytics-setings.type';
import { ELanguages } from '@common/enums';

import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { SettingsBreadcrumbsComponent } from '../web-settings/settings-breadcrumbs/settings-breadcrumbs.component';
import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';

type GoogleSettingsValue = NonNullable<AnalyticsSettingsLegacy['googleSettings']>;

/**
 * Signal twin of `web-analytics-settings` — value-in (`AnalyticsSettingsLegacy`
 * from `@sites/entity-core`) / `valueChangeEvent`-out, with the Google/Pixel
 * sub-blocks inlined (they were thin FormGroup relays). Keeps the original's
 * site-settings-driven force-off behaviour and the currency↔value
 * cross-validation hints. Save/cancel are emitted up — the parent owns the
 * transaction. Bound to the legacy widget shape, like the other `*-signal-legacy`
 * editors; the FormGroup original stays for shop-cart and login-edu.
 */
@Component({
    selector: 'web-analytics-settings-signal-legacy',
    templateUrl: './web-analytics-settings-signal-legacy.component.html',
    styleUrls: ['../web-analytics-settings/web-analytics-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, TranslateModule, SettingsBreadcrumbsComponent, SpSwitchComponent],
})
export class WebAnalyticsSettingsSignalLegacyComponent {
    private readonly settingsFacade = inject(SettingsFacade);
    private readonly translate = inject(TranslateService);

    public readonly settings = input<AnalyticsSettingsLegacy | null>(null);
    public readonly pixelAnalyticType = input<EPixelTypes>(EPixelTypes.lead);
    public readonly showFullSettings = input<boolean>(true);

    @Output() public valueChangeEvent: EventEmitter<AnalyticsSettingsLegacy> = new EventEmitter();
    @Output() public cancelEvent: EventEmitter<void> = new EventEmitter();

    public readonly websiteSettings$ = this.settingsFacade.siteSettings$;
    protected readonly pixelTypes = EPixelTypes;
    public readonly pixelOptions = Object.values(EPixelTypes);

    private readonly model = signal<AnalyticsSettingsLegacy | null>(null);
    private siteSettingsApplied = false;

    public readonly googleAnalytic = computed(() => Boolean(this.model()?.googleAnalytic));
    public readonly pixelAnalytic = computed(() => Boolean(this.model()?.pixelAnalytic));
    public readonly googleSettings = computed<GoogleSettingsValue>(() => ({
        category: null,
        action: null,
        label: null,
        ...this.model()?.googleSettings,
    }));
    public readonly pixelType = computed(() => this.model()?.pixelSettings?.type ?? null);

    /** currency↔value must be set together — the FormGroup cross-validator. */
    public readonly currencyInvalid = computed(() => {
        const { currency, value } = this.googleSettings();
        return Boolean(value) && !currency;
    });
    public readonly valueInvalid = computed(() => {
        const { currency, value } = this.googleSettings();
        return Boolean(currency) && !value;
    });

    constructor() {
        effect(() => {
            const incoming = this.settings();
            if (!incoming) return;
            this.model.set(incoming);
            this.applySiteSettingsOnce();
        });
    }

    /**
     * Mirror of the original's ngOnInit: when the site has no GA / Pixel
     * credentials configured, force the toggles off once on open.
     */
    private applySiteSettingsOnce(): void {
        if (this.siteSettingsApplied) return;
        this.siteSettingsApplied = true;

        this.websiteSettings$.pipe(take(1)).subscribe((siteSettings) => {
            if (!siteSettings.google_analytics && this.googleAnalytic()) this.toggleGoogleAnalytic(false);
            if (!siteSettings.facebook_pixel && this.pixelAnalytic()) this.togglePixelAnalytic(false);
        });
    }

    // --- Toggles ---

    public toggleGoogleAnalytic(value: boolean): void {
        this.patch({ googleAnalytic: value });
    }

    public togglePixelAnalytic(value: boolean): void {
        const patch: Partial<AnalyticsSettingsLegacy> = { pixelAnalytic: value };
        if (value) {
            patch.pixelSettings = { ...this.model()?.pixelSettings, type: this.pixelAnalyticType() };
        }
        this.patch(patch);
    }

    // --- Google settings ---

    public onCurrencyChange(event: Event): void {
        this.patchGoogle({ currency: (event.target as HTMLInputElement).value || null });
    }

    public onValueChange(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        this.patchGoogle({ value: raw === '' ? null : Number(raw) });
    }

    public onLeadSourceChange(event: Event): void {
        this.patchGoogle({ leadSource: (event.target as HTMLInputElement).value });
    }

    // --- Pixel settings ---

    public onPixelTypeChange(event: Event): void {
        this.patch({
            pixelSettings: {
                ...this.model()?.pixelSettings,
                type: (event.target as HTMLSelectElement).value as EPixelTypes,
            },
        });
    }

    // --- Links ---

    public get helpGALink(): string {
        return `https://sendpulse.com/${this.lang}knowledge-base/landing-page/statistics-and-analytics/connect-google-analytics`;
    }

    public get helpPixelLink(): string {
        return `https://sendpulse.com/${this.lang}knowledge-base/landing-page/statistics-and-analytics/connect-facebook-pixel`;
    }

    private get lang(): string {
        const languages = [ELanguages.RU, ELanguages.UA];

        if (this.translate.currentLang === ELanguages.PT_BR) return 'br/';

        return languages.includes(this.translate.currentLang as ELanguages) ? `${this.translate.currentLang}/` : '';
    }

    public onCancel(): void {
        this.cancelEvent.emit();
    }

    private patchGoogle(patch: Partial<GoogleSettingsValue>): void {
        this.patch({ googleSettings: { ...this.googleSettings(), ...patch } });
    }

    private patch(patch: Partial<AnalyticsSettingsLegacy>): void {
        const current = this.model();
        if (!current) return;
        const next = { ...current, ...patch };
        this.model.set(next);
        this.valueChangeEvent.emit(next);
    }
}
